import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Download,
  AlertTriangle,
  Link2,
  Check,
  X,
  Pencil,
  Copy,
  History,
  XCircle,
  MoreHorizontal,
  Truck,
  Ship,
  Plane,
  Train,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { PageHeader, MetricCard, StatusBadge, DataTable, KPIStrip } from '@/components/common';
import type { Column } from '@/components/common';
import {
  rateEngineApi,
  CarrierRate,
  RateFilterDto,
  ChargeCategory,
  TransportMode,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { format } from 'date-fns';

const safeFormatDate = (
  date: string | Date | null | undefined,
  formatStr: string,
  fallback = '-'
): string => {
  if (!date) return fallback;
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return fallback;
    return format(d, formatStr);
  } catch {
    return fallback;
  }
};

const MODE_ICONS: Record<string, React.ReactNode> = {
  Ocean: <Ship className="w-4 h-4" />,
  Air: <Plane className="w-4 h-4" />,
  TruckFTL: <Truck className="w-4 h-4" />,
  TruckLTL: <Truck className="w-4 h-4" />,
  Rail: <Train className="w-4 h-4" />,
  Intermodal: <Truck className="w-4 h-4" />,
};

const CHARGE_CATEGORIES: { value: ChargeCategory; label: string }[] = [
  { value: 'BASE_FREIGHT', label: 'Base Freight' },
  { value: 'FUEL', label: 'Fuel' },
  { value: 'ACCESSORIAL', label: 'Accessorial' },
  { value: 'HANDLING', label: 'Handling' },
  { value: 'DOCUMENTATION', label: 'Documentation' },
  { value: 'CUSTOMS', label: 'Customs' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'OTHER', label: 'Other' },
];

const TRANSPORT_MODES: { value: TransportMode; label: string }[] = [
  { value: 'Ocean', label: 'Ocean' },
  { value: 'Air', label: 'Air' },
  { value: 'TruckFTL', label: 'Truck FTL' },
  { value: 'TruckLTL', label: 'Truck LTL' },
  { value: 'Rail', label: 'Rail' },
  { value: 'Intermodal', label: 'Intermodal' },
];

export default function Rates() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [rates, setRates] = useState<CarrierRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Filters from URL
  const filters: RateFilterDto = useMemo(
    () => ({
      carrierId: searchParams.get('carrierId') || undefined,
      mode: (searchParams.get('mode') as TransportMode) || undefined,
      chargeCategory: (searchParams.get('chargeCategory') as ChargeCategory) || undefined,
      activeOnly: searchParams.get('activeOnly') === 'true' || undefined,
      expiringSoon: searchParams.get('expiringSoon') === 'true' || undefined,
      contractOnly: searchParams.get('contractOnly') === 'true' || undefined,
    }),
    [searchParams]
  );

  // Modal states
  const [expireModalOpen, setExpireModalOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState<CarrierRate | null>(null);
  const [expiring, setExpiring] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const setFilter = (key: keyof RateFilterDto, value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters =
    filters.carrierId ||
    filters.mode ||
    filters.chargeCategory ||
    filters.activeOnly ||
    filters.expiringSoon ||
    filters.contractOnly;

  const loadRates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rateEngineApi.getRates(filters);
      // Handle both paginated { data: [] } and direct array responses
      const ratesData = Array.isArray(response) ? response : response?.data || [];
      setRates(ratesData);
    } catch (error) {
      console.error('Failed to load rates:', error);
      // Set empty array on error to prevent crashes
      setRates([]);
      // Only show toast for non-404 errors (backend may not be implemented yet)
      const errorMessage = getErrorMessage(error);
      if (!errorMessage.includes('404') && !errorMessage.includes('Not Found')) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  // Calculate counts
  const activeCount = useMemo(() => rates.filter((r) => r.isActive).length, [rates]);

  const expiringCount = useMemo(() => {
    return rates.filter((r) => {
      if (!r.expirationDate || !r.isActive) return false;
      const daysUntil = Math.ceil(
        (new Date(r.expirationDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      );
      return daysUntil > 0 && daysUntil <= 30;
    }).length;
  }, [rates]);

  const contractCount = useMemo(() => rates.filter((r) => r.isContractRate).length, [rates]);

  // Copy shareable link
  const handleCopyLink = useCallback(() => {
    const shareUrl = window.location.href;
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        setLinkCopied(true);
        toast.success('Link copied! Share this URL to show others this filtered view.');
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(() => {
        toast.error('Failed to copy link');
      });
  }, []);

  const handleDuplicate = useCallback(
    async (rate: CarrierRate) => {
      try {
        await rateEngineApi.duplicateRate(rate.id);
        toast.success('Rate duplicated successfully');
        loadRates();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    },
    [loadRates]
  );

  const handleExpireClick = (rate: CarrierRate) => {
    setSelectedRate(rate);
    setExpireModalOpen(true);
  };

  const handleExpireConfirm = async () => {
    if (!selectedRate) return;

    setExpiring(true);
    try {
      await rateEngineApi.updateRate(selectedRate.id, {
        expirationDate: new Date().toISOString(),
        isActive: false,
      });
      setExpireModalOpen(false);
      setSelectedRate(null);
      loadRates();
      toast.success('Rate expired successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setExpiring(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const headers = [
        'SKU',
        'Carrier',
        'Lane',
        'Rate Type',
        'Base Rate',
        'Effective Date',
        'Expiration Date',
        'Status',
      ];

      const rows = rates.map((rate) => [
        rate.rateSKU || '',
        rate.carrier?.name || '',
        rate.lane
          ? `${rate.lane.originCity}, ${rate.lane.originState} → ${rate.lane.destCity}, ${rate.lane.destState}`
          : '',
        rate.rateType,
        rate.baseRate.toFixed(2),
        safeFormatDate(rate.effectiveDate, 'MM/dd/yyyy', ''),
        safeFormatDate(rate.expirationDate, 'MM/dd/yyyy', ''),
        rate.isActive ? 'Active' : 'Inactive',
      ]);

      const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rates-export-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Exported ${rates.length} rates to CSV`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };

  // Column definitions for DataTable
  const rateColumns: Column<CarrierRate>[] = useMemo(
    () => [
      {
        key: 'rateSKU',
        header: 'SKU',
        sortable: true,
        render: (rate) => <span className="font-mono text-sm">{rate.rateSKU || '-'}</span>,
      },
      {
        key: 'carrier',
        header: 'Carrier',
        sortable: true,
        render: (rate) => (
          <div>
            <div className="font-medium">{rate.carrier?.name}</div>
            <div className="text-xs text-muted-foreground">{rate.carrier?.code}</div>
          </div>
        ),
      },
      {
        key: 'lane',
        header: 'Lane',
        render: (rate) =>
          rate.lane ? (
            <div className="text-sm">
              {rate.lane.originCity}, {rate.lane.originState} → {rate.lane.destCity},{' '}
              {rate.lane.destState}
            </div>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        key: 'mode',
        header: 'Mode',
        sortable: true,
        render: (rate) =>
          rate.mode ? (
            <div className="flex items-center gap-1.5">
              {MODE_ICONS[rate.mode]}
              <span className="text-sm">{rate.mode}</span>
            </div>
          ) : null,
      },
      {
        key: 'chargeCategory',
        header: 'Category',
        sortable: true,
        render: (rate) => (
          <Badge variant="outline">
            {CHARGE_CATEGORIES.find((c) => c.value === rate.chargeCategory)?.label ||
              rate.chargeCategory}
          </Badge>
        ),
      },
      {
        key: 'baseRate',
        header: 'Base Rate',
        sortable: true,
        className: 'text-right',
        render: (rate) => (
          <div className="text-right">
            <div className="font-medium">${rate.baseRate.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">
              {rate.rateType} &bull; {rate.currency}
            </div>
          </div>
        ),
      },
      {
        key: 'effectiveDate',
        header: 'Effective',
        sortable: true,
        render: (rate) => {
          const isExpiring =
            rate.expirationDate &&
            rate.isActive &&
            Math.ceil(
              (new Date(rate.expirationDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
            ) <= 30;
          return (
            <div>
              <div className="text-sm">{safeFormatDate(rate.effectiveDate, 'MMM d, yyyy')}</div>
              {rate.expirationDate && (
                <div
                  className={`text-xs ${isExpiring ? 'text-status-warning-foreground' : 'text-muted-foreground'}`}
                >
                  Exp: {safeFormatDate(rate.expirationDate, 'MMM d, yyyy')}
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: 'isActive',
        header: 'Status',
        sortable: true,
        render: (rate) => (
          <div className="flex flex-col gap-1">
            <StatusBadge
              status={rate.isActive ? 'active' : 'inactive'}
              label={rate.isActive ? 'Active' : 'Inactive'}
            />
            {rate.isContractRate && <StatusBadge status="approved" label="Contract" />}
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        className: 'w-20',
        render: (rate) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="More actions">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDuplicate(rate)}>
                <Copy className="w-4 h-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem>
                <History className="w-4 h-4 mr-2" />
                Version History
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {rate.isActive && (
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => handleExpireClick(rate)}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Expire Rate
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [handleDuplicate]
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title="Rate Management"
        description="Contracted freight rates by carrier and lane, with child charges and slab pricing."
        actions={
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button variant="ghost" onClick={handleCopyLink} title="Copy shareable link">
                {linkCopied ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-status-success" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4 mr-2" />
                    Share View
                  </>
                )}
              </Button>
            )}
            <Button variant="outline" onClick={handleExport} disabled={exporting}>
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Rate
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <div className={cn(PANEL, 'p-4')}>
        <div className="flex flex-wrap items-center gap-4">
          <Select
            value={filters.mode || '_all_'}
            onValueChange={(v) => setFilter('mode', v === '_all_' ? undefined : v)}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Modes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all_">All Modes</SelectItem>
              {TRANSPORT_MODES.map((mode) => (
                <SelectItem key={mode.value} value={mode.value}>
                  {mode.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.chargeCategory || '_all_'}
            onValueChange={(v) => setFilter('chargeCategory', v === '_all_' ? undefined : v)}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all_">All Categories</SelectItem>
              {CHARGE_CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={filters.activeOnly || false}
                onCheckedChange={(checked) => setFilter('activeOnly', checked ? 'true' : undefined)}
              />
              Active Only
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={filters.expiringSoon || false}
                onCheckedChange={(checked) =>
                  setFilter('expiringSoon', checked ? 'true' : undefined)
                }
              />
              Expiring Soon
              {expiringCount > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-status-warning-subtle text-status-warning-foreground"
                >
                  {expiringCount}
                </Badge>
              )}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={filters.contractOnly || false}
                onCheckedChange={(checked) =>
                  setFilter('contractOnly', checked ? 'true' : undefined)
                }
              />
              Contract Only
              {contractCount > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-status-info-subtle text-status-info-foreground"
                >
                  {contractCount}
                </Badge>
              )}
            </label>
          </div>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-destructive hover:text-destructive"
            >
              <X className="w-4 h-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <KPIStrip columns={4}>
        <MetricCard label="Total Rates" value={rates.length} />
        <MetricCard
          label="Active Rates"
          value={activeCount}
          trend={activeCount > 0 ? { direction: 'up', value: '', text: 'Active rates' } : undefined}
        />
        <MetricCard
          label="Expiring Soon"
          value={expiringCount}
          trend={
            expiringCount > 0 ? { direction: 'down', value: '', text: 'Within 30 days' } : undefined
          }
        />
        <MetricCard label="Contract Rates" value={contractCount} />
      </KPIStrip>

      {/* Rate Table */}
      <DataTable
        columns={rateColumns}
        data={rates}
        loading={loading}
        pagination
        pageSize={50}
        searchable
        searchPlaceholder="Search rates..."
        exportable
        exportFilename="rates-export"
        columnToggle
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowId={(rate) => rate.id}
        emptyTitle="No Rates Found"
        emptyDescription="No rates match your current filters."
        caption="Rate management table"
      />

      {/* Bulk Actions Toolbar */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-card border rounded-xl shadow-floating p-4 flex items-center gap-4">
          <span className="text-sm font-medium">{selectedIds.length} rate(s) selected</span>
          <Button variant="outline" size="sm">
            <Copy className="w-4 h-4 mr-2" />
            Duplicate
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button variant="destructive" size="sm">
            <XCircle className="w-4 h-4 mr-2" />
            Expire
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Expire Confirmation Modal */}
      <Dialog open={expireModalOpen} onOpenChange={setExpireModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-status-destructive-subtle rounded-full">
                <AlertTriangle className="w-5 h-5 text-status-destructive-foreground" />
              </div>
              Expire Rate?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to expire this rate? This will set the expiration date to today
              and mark the rate as inactive.
            </DialogDescription>
          </DialogHeader>

          {selectedRate && (
            <div className="p-3 bg-muted rounded-lg text-sm">
              <div className="font-medium">{selectedRate.rateSKU || 'No SKU'}</div>
              <div className="text-muted-foreground">
                {selectedRate.carrier?.name} - ${selectedRate.baseRate.toFixed(2)}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setExpireModalOpen(false);
                setSelectedRate(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleExpireConfirm} disabled={expiring}>
              {expiring ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Expire Rate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
